import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    Admin,
    DataProvider,
    fetchUtils,
    GetOneParams,
    Layout,
    List,
    Resource,
    useDelete,
    useListController,
    useRedirect
} from "react-admin";
import jsonServerProvider from "ra-data-json-server";
import {BASE_URL} from "../../../../utils/Utils";
import {adminHttpClient} from "../../../../components/organisms/OrdersAdmin/OrdersAdmin";
import UserEdit from "../../../../components/organisms/UsersAdmin/UserEdit";
import UserShow from "../../../../components/organisms/UsersAdmin/UserShow";
import {Center, HStack, Text, VStack} from "@chakra-ui/react";
import {SectionHeader} from "../../../../components/molecules/SectionHeader/SectionHeader";
import {createTheme} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {DeleteIcon, EditIcon} from "@chakra-ui/icons";
import {UserModelResponse} from "../../../../data/model/LoginResponse";
import {DataGrid} from "@mui/x-data-grid";
import {useDialogStore} from "../../../../store/useAlertDialog";
import polyglotI18nProvider from "ra-i18n-polyglot";
import {FilterSidebar} from "../../../../components/molecules/FilterSidebar/FilterSidebar";
import {useUserStore} from "../../../../store/use-user-store";
import {EmptyPage} from "../../../../components/organisms/EmptyPage/EmptyPage";

const italianMessages = require("ra-language-italian");

const dataProvider = jsonServerProvider(BASE_URL, adminHttpClient);
const i18nProvider = polyglotI18nProvider(() => italianMessages, "it");

const customDataProvider: DataProvider = {
    ...dataProvider,

    getList: (resource, params) => {
        const {page, perPage} = params.pagination || {};
        const {field, order} = params.sort || {};
        const start = ((page || 1) - 1) * (perPage || 10);
        const end = (page || 1) * (perPage || 10);

        const query = {
            _start: start,
            _end: end,
            _sort: field,
            _order: order,
        };

        const url = `${BASE_URL}/${resource}?${fetchUtils.queryParameters(query)}`;

        return adminHttpClient(url).then(({headers, json}) => {
            let total: number | undefined;
            if (headers) {
                const totalHeader = headers.get("X-Total-Count") || 0;
                total = totalHeader ? parseInt(totalHeader, 10) : undefined;
            }
            if (total === undefined || isNaN(total)) {
                total = json.total || (Array.isArray(json) ? json.length : 0);
            }

            const dataArr = Array.isArray(json) ? json : json.data;

            return {
                data: dataArr.map((item: any) => ({
                    id: item.orderId || item.id,
                    ...item,
                })),
                total,
            };
        });
    },

    getOne: async (resource: string, params: GetOneParams) => {
        const url = `${BASE_URL}/${resource}/${params.id}`;
        const {json} = await adminHttpClient(url);
        if (!json || (!json.id && !json.orderId)) {
            throw new Error("Invalid response format: missing 'id'");
        }
        return {data: {id: json.orderId || json.id, ...json}};
    },

    getMany: async (resource, params) => {
        const query = {ids: params.ids.join(",")};
        const url = `${BASE_URL}/${resource}?${fetchUtils.queryParameters(query)}`;

        const {json} = await adminHttpClient(url);
        return ({
            data: json.data.map((item: any) => ({
                id: item.orderId || item.id,
                ...item,
            })),
        });
    },

    getManyReference: async (resource, params) => {
        const {page, perPage} = params.pagination;
        const query = {
            [params.target]: params.id,
            page,
            limit: perPage,
            sort: params.sort?.field,
            order: params.sort?.order,
        };

        const url = `${BASE_URL}/${resource}?${fetchUtils.queryParameters(query)}`;

        const {json} = await adminHttpClient(url);
        return ({
            data: json.data.map((item: any) => ({
                id: item.orderId || item.id,
                ...item,
            })),
            total: json.total,
        });
    },

    update: async (resource, params) => {
        const url = `${BASE_URL}/${resource}/${params.id}`;
        const {json} = await adminHttpClient(url, {
            method: "PUT",
            body: JSON.stringify(params.data),
        });
        if (!json || !json.data || !json.data.id) {
            console.error("Invalid response format for update:", json);
            return {data: {id: params.id, ...params.data}}; // Fallback
        }
        return {data: json.data};
    },

    create: async (resource, params) => {
        const url = `${BASE_URL}/${resource}`;
        const {json} = await adminHttpClient(url, {
            method: "POST",
            body: JSON.stringify(params.data),
        });
        if (!json || !json.data || !json.data.id) {
            throw new Error("Invalid response format for create");
        }
        return {data: json.data};
    },

    delete: async (resource, params) => {
        const url = `${BASE_URL}/${resource}/${params.id}`;
        const {json} = await adminHttpClient(url, {method: "DELETE"});
        return {data: json?.data ?? {...params.previousData}};
    },

    deleteMany: async (resource, params) => {
        const url = `${BASE_URL}/${resource}`;
        await adminHttpClient(url, {
            method: "DELETE",
            body: JSON.stringify({ids: params.ids}),
        });
        return ({data: params.ids});
    },

    updateMany: async (resource, params) => {
        const url = `${BASE_URL}/${resource}`;
        const {json} = await adminHttpClient(url, {
            method: "PUT",
            body: JSON.stringify({ids: params.ids, data: params.data}),
        });
        if (!json || !Array.isArray(json.data)) {
            throw new Error("Invalid response format for updateMany");
        }
        return {data: json.data.map((item: any) => item.id)};
    },
};

const customStyles = {
    // "& .RaLayout-appFrame": {
    //     margin: 0,
    //     padding: 0,
    // },
};

const CustomLayout = (props: any) => {
    const {searchText, setSearchText} = useUserStore();

    return (
        <HStack w={"100%"} h={"100%"} overflow={"hidden"}>
            {/*<VStack width={"70%"} position={"relative"} maxWidth={"100%"} overflow={"hidden"} h={"100%"} pt={"24px"}>*/}
            {/*    <SectionHeader title={"Utenti"}/>*/}
            {/*    <HStack alignSelf={'stretch'} flex={1} overflowY={'auto'} overflowX={'auto'}>*/}
            {/*        <Layout appBar={() => null} sx={customStyles} appBarAlwaysOn={false}*/}
            {/*            sidebar={() => null} {...props} />*/}
            {/*    </HStack>*/}
            {/*</VStack>*/}
            <VStack position={"relative"} width={"70%"} pt={"24px"} height={"100%"} alignSelf={'stretch'}>
                <SectionHeader title={"Utenti"}/>
                <HStack alignSelf={'stretch'} flex={1} overflowY={'auto'} overflowX={'auto'}>
                    <Layout appBar={() => null} appBarAlwaysOn={false}
                            sidebar={() => null} {...props} />
                </HStack>
            </VStack>
            <FilterSidebar
                searchProps={{
                    placeholder: "Inserisci cosa vuoi cercare",
                    searchInput: searchText,
                    onChangeText: setSearchText
                }}
                buttons={{
                    cancel: {
                        text: "Annulla",
                        onClick: () => {
                            setSearchText("")
                        }
                    }
                }}
                stackProps={{
                    width: "30%",
                    height: '100%',
                    alignSelf: 'stretch',
                    backgroundColor: "surfaceContainer.light",
                }}/>
        </HStack>);
}

const theme = createTheme({
    palette: {
        background: {
            default: "#f5fbf5"
        }
    },
    components: {
        MuiToolbar: {
            styleOverrides: {
                root: {
                    display: 'none'
                }
            }
        }
    },
}/*, itIT, coreItIT)*/);

const UserListCustom = (props: any) => {
    const {data: controllerData, isLoading} = useListController(props);
    const data: UserModelResponse[] | undefined = controllerData as (UserModelResponse[] | undefined);
    const [deleteOne] = useDelete();

    const gridRef = useRef<HTMLDivElement>(null);
    const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});

    useEffect(() => {
        const calculatePageSize = () => {
            if (gridRef.current) {
                const availableHeight = gridRef.current.clientHeight;
                const rowHeight = 52; // Altezza stimata di una riga in MUI DataGrid
                const calculatedPageSize = Math.max(1, Math.floor(availableHeight / rowHeight));
                setPaginationModel(prev => ({...prev, pageSize: calculatedPageSize}));
            }
        };

        calculatePageSize();

        window.addEventListener("resize", calculatePageSize);
        return () => window.removeEventListener("resize", calculatePageSize);
    }, []);

    const {searchText} = useUserStore();

    const filteredData = useMemo(() => {
        if (!data) return [];
        return data.filter((user: UserModelResponse) =>
            user.email?.trim().toLowerCase().includes(searchText.trim().toLowerCase()) ||
            user.id?.toString().includes(searchText)
        );
    }, [data, searchText]);

    const {showDialog, closeDialog} = useDialogStore();
    const redirect = useRedirect();

    const columns = [
        {field: 'id', headerName: 'ID', flex: 1},
        {field: 'email', headerName: 'Email', flex: 1},
        {field: 'userType', headerName: 'Tipo Utente', flex: 1},
        {
            field: 'isVerified',
            headerName: 'Email Verificata',
            flex: 1,
            renderCell: (params: any) => params.value ? "✔️" : "❌",
        },
        {field: 'verificationCode', headerName: 'Codice di Verifica', flex: 1},
        {
            field: 'actions',
            headerName: 'Azioni',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: any) => {
                return (
                    <div>
                        <IconButton onClick={() => {
                            redirect(`/users/${params.row.id}/edit`);
                        }} aria-label="Edit">
                            <EditIcon color="primary"/>
                        </IconButton>
                        <IconButton onClick={() => showDialog({
                            title: "Elimina utente",
                            description: "Sei sicuro di voler eliminare questo utente?",
                            cancelButtonText: "Annulla",
                            confirmButtonText: "Elimina",
                            onPressCancelButton: () => closeDialog(),
                            onConfirmButtonClick: () => {
                                deleteOne('users', {id: params.row.id}).then(() => {
                                    closeDialog();
                                });
                            }
                        })}>
                            <DeleteIcon color="error"/>
                        </IconButton>
                    </div>
                );
            },
        }
    ];

    const transformData = (data?: UserModelResponse[]) => {
        return data?.map((user: UserModelResponse) => ({
            id: user.id,
            email: user.email,
            userType: user.userType,
            isVerified: user.isVerified,
            user: user,
        }));
    };

    const rows = transformData(filteredData);

    if (!data || data.length === 0) {
        return (
            <EmptyPage isLoading={isLoading} title={"Nessun utente trovato"}
                       description={"Prova a cambiare i filti o ad aggiungere nuovi utenti"}/>
        )
    }

    return (
        <div ref={gridRef} style={{display: "flex", flexDirection: "column", height: "100%", width: "100%"}}>
            <List {...props}>
                <DataGrid
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            background: 'white !important',
                        },
                        '& .MuiDataGrid-row--borderBottom': {
                            background: 'white !important',
                        },
                        '& .MuiToolbar-root': {
                            display: 'flex !important'
                        }
                    }}
                    pagination
                    paginationMode="client"
                    rows={rows}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    // autoPageSize={false}
                />
            </List>
        </div>
    );
};

export default function UsersPage() {
    return (
        <Admin dataProvider={customDataProvider} basename={'/users'} i18nProvider={i18nProvider} layout={CustomLayout} theme={theme}
               title={"Utenti"}>
            <Resource name="users" list={(props) => UserListCustom(props)} edit={UserEdit} show={UserShow}/>
        </Admin>
    )
}
